import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowRight,
  Boxes,
  KeyRound,
  ShieldCheck,
} from 'lucide-react'
import { useAuth } from '@/lib/auth/useAuth'
import { PageHeader } from '@/components/page-header'
import { Eyebrow, Hairline } from '@/components/hawk-primitives'

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardPage,
})

function DashboardPage() {
  const { user, roles } = useAuth()
  const greeting = getGreeting()
  const firstName = user?.name?.split(' ')[0] ?? 'User'
  const uniqueRoles = [...new Set(roles)]
  const uniqueGroups = [...new Set(user?.groups ?? [])]

  return (
    <div className="px-10 py-8">
      <PageHeader
        title={`${greeting}, ${firstName}.`}
        description="Your personal snapshot of identity, memberships, and what to do next."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2">
          <div className="mb-4">
            <Eyebrow>Identity</Eyebrow>
          </div>
          <IdentityPanel
            user={user}
            roles={uniqueRoles}
            groups={uniqueGroups}
          />
        </section>

        <section>
          <div className="mb-4">
            <Eyebrow>Quick actions</Eyebrow>
          </div>
          <ActionStack />
        </section>
      </div>

      <section className="mt-10">
        <div className="mb-4">
          <Eyebrow>Activity</Eyebrow>
        </div>
        <EmptyActivity />
      </section>
    </div>
  )
}

function IdentityPanel({
  user,
  roles,
  groups,
}: {
  user: ReturnType<typeof useAuth>['user']
  roles: string[]
  groups: string[]
}) {
  const initial = (user?.name ?? user?.email ?? 'U').charAt(0).toUpperCase()
  return (
    <div
      className="relative border border-border bg-card rounded-md p-7 animate-hawk-rise overflow-hidden"
      style={{ animationDelay: '0ms' }}
    >
      <div
        className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
        aria-hidden
      />
      <div className="flex items-start gap-5 mb-6">
        <div className="shrink-0">
          <div className="size-16 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center text-3xl text-primary font-medium">
            {initial}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-2xl font-semibold tracking-tight text-foreground leading-tight mb-1">
            {user?.name ?? 'User'}
          </div>
          <div className="font-mono text-xs text-muted-foreground truncate">
            {user?.email ?? '—'}
          </div>
        </div>
      </div>

      <Hairline label="Attributes" />

      <dl className="mt-5 grid grid-cols-[120px_1fr] gap-y-3.5 text-sm">
        <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground pt-1">
          Subject
        </dt>
        <dd
          className="font-mono text-xs text-foreground/80 break-all"
          title={user?.sub ?? ''}
        >
          {user?.sub ?? '—'}
        </dd>

        <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground pt-1">
          Roles
        </dt>
        <dd className="flex flex-wrap gap-1.5">
          {roles.length > 0 ? (
            roles.map((role) => (
              <span
                key={role}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-primary/10 text-primary ring-1 ring-inset ring-primary/30"
              >
                <span className="size-1 rounded-full bg-primary" />
                {role}
              </span>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">None assigned</span>
          )}
        </dd>

        <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground pt-1">
          Groups
        </dt>
        <dd className="flex flex-wrap gap-1.5">
          {groups.length > 0 ? (
            groups.map((group) => (
              <span
                key={group}
                className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-secondary text-foreground/70 ring-1 ring-inset ring-border"
              >
                {group}
              </span>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </dd>
      </dl>
    </div>
  )
}

function ActionStack() {
  const actions = [
    {
      to: '/packages' as const,
      label: 'Browse packages',
      sub: 'Repositories and install commands',
      icon: Boxes,
    },
    {
      to: '/settings/security' as const,
      label: 'Security keys',
      sub: 'Register hardware authenticators',
      icon: KeyRound,
    },
    {
      to: '/settings' as const,
      label: 'Profile settings',
      sub: 'Identity and account details',
      icon: ShieldCheck,
    },
  ]
  return (
    <div
      className="border border-border bg-card rounded-md overflow-hidden animate-hawk-rise"
      style={{ animationDelay: '80ms' }}
    >
      {actions.map((action, i) => {
        const Icon = action.icon
        return (
          <Link
            key={action.to}
            to={action.to}
            className={`group relative flex items-center gap-4 px-5 py-4 hover:bg-popover transition-colors ${
              i > 0 ? 'border-t border-border' : ''
            }`}
          >
            <span className="absolute left-0 top-3 bottom-3 w-[2px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="size-9 rounded-md bg-secondary border border-border flex items-center justify-center group-hover:border-primary/40 transition-colors">
              <Icon
                className="size-4 text-muted-foreground group-hover:text-primary transition-colors"
                strokeWidth={1.5}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">
                {action.label}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {action.sub}
              </div>
            </div>
            <ArrowRight className="size-4 text-muted-foreground/50 group-hover:translate-x-0.5 group-hover:text-foreground transition-all" />
          </Link>
        )
      })}
    </div>
  )
}

function EmptyActivity() {
  return (
    <div
      className="relative overflow-hidden border border-border border-dashed rounded-md py-14 px-8 text-center bg-card/30 animate-hawk-rise"
      style={{ animationDelay: '160ms' }}
    >
      <div
        className="absolute inset-0 hawk-grid hawk-fade-radial pointer-events-none"
        aria-hidden
      />
      <div className="relative">
        <p className="font-display text-xl tracking-tight mb-2 text-foreground/80">
          Quiet on the wire.
        </p>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          Activity from packages you publish or download will surface here.
        </p>
      </div>
    </div>
  )
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 5) return 'Late night'
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}
